import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Phone, MessageCircle, MapPin, CalendarDays, ArrowLeft } from "lucide-react";
import { api, waLink, telLink, resolveImg } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1681398838063-058757095ef1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Landing() {
  const navigate = useNavigate();
  const { data: settings } = useQuery({ queryKey: ["pub-settings"], queryFn: async () => (await api.get("/public/settings")).data });
  const { data: venues } = useQuery({ queryKey: ["pub-venues"], queryFn: async () => (await api.get("/public/venues")).data });

  return (
    <div className="min-h-screen gradient-navy text-offwhite">
      {/* Hero */}
      <section className="relative min-h-[92vh] flex flex-col items-center justify-center text-center px-6 overflow-hidden">
        <img src={HERO} alt="قمرية" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/60 to-navy" />
        <div className="relative z-10 animate-fade-up">
          <p className="tracking-[0.5em] text-gold text-sm sm:text-base mb-6">QAMARIA</p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-tight mb-6 max-w-3xl mx-auto">
            {settings?.hero_title || "ليلتك الاستثنائية... تبدأ من هنا"}
          </h1>
          <div className="gold-divider w-40 mx-auto mb-10" />
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button data-testid="book-now-btn" onClick={() => navigate("/booking")}
              className="btn-gold px-10 py-4 rounded-full text-lg w-full sm:w-auto min-w-[200px]">
              احجز مناسبتك
            </button>
            <button data-testid="discover-dates-btn" onClick={() => navigate("/booking")}
              className="px-10 py-4 rounded-full text-lg border border-gold/50 text-gold hover:bg-gold/10 transition-colors w-full sm:w-auto min-w-[200px]">
              اكتشف المواعيد المتاحة
            </button>
          </div>
        </div>
      </section>

      {/* Venues */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="text-center mb-14">
          <h2 className="font-display text-3xl sm:text-4xl mb-3">أماكننا</h2>
          <p className="text-muted2">اختر المكان الذي يليق بمناسبتك</p>
        </div>
        <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
          {(venues || []).map((v) => (
            <div key={v.id} data-testid={`venue-card-${v.id}`}
              className="group card-lux overflow-hidden cursor-pointer" onClick={() => navigate("/booking")}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={resolveImg(v.images?.[0])} alt={v.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-transparent" />
                <h3 className="absolute bottom-4 start-5 font-display text-2xl sm:text-3xl text-offwhite">{v.name}</h3>
              </div>
              <div className="p-6">
                <p className="text-muted2 text-sm leading-relaxed mb-4">{v.description}</p>
                <span className="inline-flex items-center gap-2 text-gold text-sm font-semibold">
                  احجز الآن <ArrowLeft size={16} />
                </span>
              </div>
            </div>
          ))}
          {!venues?.length && <p className="text-center text-muted2 col-span-2">لا توجد أماكن متاحة حاليًا.</p>}
        </div>
      </section>

      {/* Location map */}
      {settings?.map_query && (
        <section className="max-w-4xl mx-auto px-6 md:px-12 pb-8" data-testid="map-section">
          <h2 className="font-display text-2xl text-center mb-2">موقعنا</h2>
          <p className="text-muted2 text-center text-sm mb-6">{settings?.location}</p>
          <div className="card-lux overflow-hidden rounded-2xl">
            <iframe title="موقع قمرية على خرائط قوقل" src={`https://maps.google.com/maps?q=${encodeURIComponent(settings.map_query)}&hl=ar&output=embed`} className="w-full h-72 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
          </div>
          <div className="text-center mt-4">
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings.map_query)}`} target="_blank" rel="noreferrer" data-testid="open-map-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass text-sm text-gold hover:bg-navy3 transition-colors">
              <MapPin size={16} /> افتح في خرائط قوقل
            </a>
          </div>
        </section>
      )}

      {/* Contact */}
      <footer className="border-t border-gold/10 bg-navy2/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col items-center gap-6">
          <p className="font-display text-2xl gold-text">QAMARIA</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={telLink(settings?.phone)} data-testid="contact-call" className="flex items-center gap-2 px-5 py-3 rounded-full glass text-sm hover:bg-navy3 transition-colors">
              <Phone size={16} className="text-gold" /> اتصل بنا
            </a>
            <a href={waLink(settings?.whatsapp)} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-2 px-5 py-3 rounded-full glass text-sm hover:bg-navy3 transition-colors">
              <MessageCircle size={16} className="text-gold" /> واتساب
            </a>
            <span className="flex items-center gap-2 px-5 py-3 rounded-full glass text-sm">
              <MapPin size={16} className="text-gold" /> {settings?.location}
            </span>
          </div>
          <button onClick={() => navigate("/admin/login")} data-testid="staff-login-link" className="text-muted2/50 text-xs hover:text-gold transition-colors flex items-center gap-1 mt-2">
            <CalendarDays size={12} /> دخول الإدارة
          </button>
        </div>
      </footer>
    </div>
  );
}
